import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography,
  TextField,
  List,
  ListItem,
  ListItemText,
  IconButton,
  CircularProgress,
  Card,
  CardContent,
  Paper,
  Checkbox,
  Box,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PersonIcon from '@mui/icons-material/Person';
import { useSnackbar } from 'notistack';
import _ from 'lodash';

import {
  getAffiliateAuthorizations,
  addAffiliateAuthorization,
  removeAffiliateAuthorization,
  updateEntityInherit,
  searchAffiliates,
  AffiliateAuthorizationResponse,
} from 'actions/AffiliateActions/AffiliateActions';

interface SearchResult {
  affiliateGuid: string;
  affiliateName: string;
  affiliateNumber: string;
}

interface Props {
  individualGuid: string;
}

const AffiliateAuthorizationManager: React.FC<Props> = ({ individualGuid }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [assigned, setAssigned] = useState<AffiliateAuthorizationResponse[]>([]);
  const [loadingAssigned, setLoadingAssigned] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);

  const fetchAssigned = () => {
    setLoadingAssigned(true);
    getAffiliateAuthorizations(individualGuid)
      .then((res) => setAssigned(res.data ?? []))
      .catch(() => enqueueSnackbar('Failed to load affiliate authorizations', { variant: 'error' }))
      .finally(() => setLoadingAssigned(false));
  };

  useEffect(() => {
    fetchAssigned();
  }, [individualGuid]);

  const debouncedSearch = useCallback(
    _.debounce((query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setSearching(false);
        return;
      }
      setSearching(true);
      searchAffiliates(query)
        .then((res) => setSearchResults(res.data ?? []))
        .catch(() => enqueueSnackbar('Affiliate search failed', { variant: 'error' }))
        .finally(() => setSearching(false));
    }, 400),
    []
  );

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    debouncedSearch(e.target.value);
  };

  const handleAdd = (result: SearchResult) => {
    addAffiliateAuthorization(individualGuid, result.affiliateGuid)
      .then(() => {
        enqueueSnackbar(`${result.affiliateName} added`, { variant: 'success' });
        setSearchQuery('');
        setSearchResults([]);
        fetchAssigned();
      })
      .catch((err) => {
        const data = err?.response?.data;
        const message = (typeof data === 'string' && data.trim())
          ? data
          : 'Failed to add affiliate authorization';
        enqueueSnackbar(message, { variant: 'error' });
      });
  };

  const handleRemove = (affiliate: AffiliateAuthorizationResponse) => {
    removeAffiliateAuthorization(individualGuid, affiliate.affiliateGuid)
      .then(() => {
        enqueueSnackbar(`${affiliate.affiliateName} removed`, { variant: 'success' });
        fetchAssigned();
      })
      .catch(() => enqueueSnackbar('Failed to remove affiliate authorization', { variant: 'error' }));
  };

  const handleEntityInheritChange = (affiliate: AffiliateAuthorizationResponse, checked: boolean) => {
    updateEntityInherit(individualGuid, affiliate.affiliateGuid, checked)
      .then(() => {
        setAssigned((prev) =>
          prev.map((a) => a.affiliateGuid === affiliate.affiliateGuid ? { ...a, entityInherit: checked } : a)
        );
      })
      .catch(() => enqueueSnackbar('Failed to update Entity Inherit', { variant: 'error' }));
  };

  const assignedGuids = new Set(assigned.map((a) => a.affiliateGuid));
  const filteredResults = searchResults.filter((r) => !assignedGuids.has(r.affiliateGuid));

  return (
    <Card variant="outlined" sx={{ borderRadius: 2, mt: 2, width: '100%' }}>
      <CardContent sx={{ p: 3 }}>
        <Grid container size={{ xs: 12 }} alignItems="center" sx={{ mb: 3 }}>
          <PersonIcon sx={{ color: '#005DA5', mr: 1 }} />
          <span style={{ color: '#005DA5', fontWeight: 500, fontSize: '18px' }}>
            Affiliate Authorizations
          </span>
        </Grid>

        {loadingAssigned ? (
          <CircularProgress size={20} />
        ) : assigned.length === 0 ? (
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            No affiliate authorizations assigned.
          </Typography>
        ) : (
          <List dense disablePadding sx={{ mb: 2 }}>
            {assigned.map((affiliate) => (
              <ListItem
                key={affiliate.affiliateGuid}
                disablePadding
                sx={{ py: 0.5, display: 'flex', alignItems: 'center' }}
              >
                <Typography variant="body2" sx={{ flex: 1 }}>{affiliate.affiliateName}</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                  <Checkbox
                    size="small"
                    checked={affiliate.entityInherit}
                    onChange={(e) => handleEntityInheritChange(affiliate, e.target.checked)}
                    sx={{ p: 0.5 }}
                  />
                  <Typography variant="body2">Inherit</Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => handleRemove(affiliate)}
                  aria-label={`Remove ${affiliate.affiliateName}`}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        )}

        <Box sx={{ mb: 2, width: '100%', borderBottom: '1px solid rgba(0, 0, 0, 0.12)' }} />

        <TextField
          fullWidth
          variant="outlined"
          label="Search by local number to add an affiliate"
          value={searchQuery}
          onChange={handleSearchChange}
          size="small"
          slotProps={{
            input: {
              endAdornment: searching ? <CircularProgress size={16} /> : null,
            },
          }}
        />

        {searchResults.length > 0 && filteredResults.length === 0 && !searching && (
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1, px: 0.5 }}>
            Already assigned.
          </Typography>
        )}

        {filteredResults.length > 0 && (
          <Paper variant="outlined" sx={{ mt: 1, maxHeight: 200, overflowY: 'auto' }}>
            <List dense disablePadding>
              {filteredResults.map((result) => (
                <ListItem
                  key={result.affiliateGuid}
                  disablePadding
                  sx={{ py: 0.5, px: 1 }}
                  secondaryAction={
                    <IconButton
                      edge="end"
                      size="small"
                      onClick={() => handleAdd(result)}
                      aria-label={`Add ${result.affiliateName}`}
                    >
                      <AddIcon fontSize="small" />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={result.affiliateName}
                    secondary={result.affiliateNumber}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        )}
      </CardContent>
    </Card>
  );
};

export default AffiliateAuthorizationManager;
